import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { CalendarCheck, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createBooking } from "../lib/api";
import { BUSINESS_TYPES } from "../data/automationData";

const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes"];
const TIMES = ["Mañana (9–12)", "Tarde (12–4)", "Noche (4–6)"];

const empty = {
  name: "",
  email: "",
  business_type: "",
  preferred_day: "",
  preferred_time: "",
  pain_point: "",
  notes: "",
};

export const BookingModal = ({ open, onOpenChange, defaults = {} }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const merged = { ...empty, ...defaults, ...form };
  const valid =
    merged.name && merged.email && merged.business_type && merged.preferred_day && merged.preferred_time;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Completa tu nombre, correo, día y hora.");
      return;
    }
    setLoading(true);
    try {
      await createBooking(merged);
      setDone(true);
      toast.success("¡Tu llamada quedó agendada!");
    } catch (err) {
      toast.error("Algo salió mal. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const close = (o) => {
    onOpenChange(o);
    if (!o) {
      setTimeout(() => {
        setForm(empty);
        setDone(false);
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="booking-modal">
        {done ? (
          <div className="py-6 text-center" data-testid="booking-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7 text-emerald-600" />
            </div>
            <h3 className="font-heading text-2xl font-semibold mb-2">¡Ya estás en la agenda!</h3>
            <p className="text-muted-foreground text-sm">
              Recibimos tu solicitud para una llamada gratis de 15 minutos. Te contactaremos a{" "}
              <span className="font-medium text-foreground">{merged.email}</span> para confirmar tu
              horario.
            </p>
            <button
              onClick={() => close(false)}
              data-testid="booking-close-btn"
              className="mt-6 bg-primary text-primary-foreground px-6 py-2.5 rounded-full font-semibold hover:bg-primary/90 transition-colors"
            >
              Listo
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2 text-xl">
                <CalendarCheck className="h-5 w-5 text-accent" /> Agenda tu llamada gratis de 15 min
              </DialogTitle>
              <DialogDescription>
                Cuéntanos un poco sobre ti y elige un horario. Es gratis y sin compromiso.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="bk-name">Tu nombre</Label>
                <Input
                  id="bk-name"
                  data-testid="booking-name-input"
                  value={merged.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Jamie Rivera"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="bk-email">Correo</Label>
                <Input
                  id="bk-email"
                  type="email"
                  data-testid="booking-email-input"
                  value={merged.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="tu@negocio.com"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Tipo de negocio</Label>
                <Select value={merged.business_type} onValueChange={(v) => set("business_type", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="booking-business-type-select">
                    <SelectValue placeholder="Elige uno" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Día preferido</Label>
                  <Select value={merged.preferred_day} onValueChange={(v) => set("preferred_day", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="booking-day-select">
                      <SelectValue placeholder="Día" />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Hora preferida</Label>
                  <Select value={merged.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="booking-time-select">
                      <SelectValue placeholder="Hora" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="bk-notes">¿En qué te gustaría que te ayudemos? (opcional)</Label>
                <Textarea
                  id="bk-notes"
                  data-testid="booking-notes-input"
                  value={merged.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  placeholder="ej. pierdo llamadas en la hora del almuerzo"
                  className="mt-1.5 resize-none"
                  rows={2}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit-btn"
                className="w-full bg-accent text-accent-foreground py-3 rounded-full font-semibold hover:bg-accent/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Agendando...
                  </>
                ) : (
                  "NO ACTIVO (pero me interesa que lo activen)"
                )}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
